/**
 * Flight model (GDD §4.1). The jet always flies upriver (−z) at the throttle's speed; steering sets a sideways
 * velocity target that the jet approaches smoothly; bank, yaw and pitch follow the motion as in the concept
 * (concept L1263–L1282). Pure: no rendering, no allocation.
 */
import { config } from '../core/config.ts';
import { clamp, damp, lerp, PERIOD } from '../core/math.ts';
import type { WorldProfile } from '../world/profiles.ts';
import type { PilotInput } from './pilot.ts';

export interface FlightState {
  /** Seconds since the flight started (drives the visual bob). */
  t: number;
  x: number;
  y: number;
  z: number;
  /** Sideways velocity (m/s, +x right). */
  vx: number;
  vy: number;
  /** Forward speed (m/s). */
  speed: number;
  roll: number;
  yaw: number;
  pitch: number;
}

/** The jet starts 20 m upriver on the centerline at cruise speed (concept L857). */
export function createFlight(W: WorldProfile): FlightState {
  const f = config.flight;
  return { t: 0, x: W.center(-20), y: f.altitude, z: -20, vx: 0, vy: 0, speed: f.cruise, roll: 0, yaw: 0, pitch: 0 };
}

/** GDD §4.1: the throttle maps linearly from 26 to 44 to 62 m/s. */
export function throttleTarget(throttle: number): number {
  const f = config.flight;
  const t = clamp(throttle, -1, 1);
  return t < 0 ? lerp(f.cruise, f.min, -t) : lerp(f.cruise, f.max, t);
}

/** Wrap point of the floating origin (concept L1277): the world repeats every 800 m. */
export const SHIFT_AT = -820;

/**
 * Advances one fixed step. `alive` false brings the jet to a stop (a crashed jet, M3).
 * Returns the floating-origin shift applied this step (0 or +800 m). `shiftAt` −Infinity never shifts (tests render
 * the unshifted twin of a run).
 */
export function stepFlight(
  s: FlightState,
  input: PilotInput,
  dt: number,
  sensitivity: number,
  alive = true,
  shiftAt = SHIFT_AT,
): number {
  const f = config.flight;
  s.t += dt;
  s.speed = damp(s.speed, alive ? throttleTarget(input.throttle) : 0, f.throttleEase, dt);
  if (alive) s.z -= s.speed * dt;
  const sens = clamp(sensitivity, f.sensitivity.min, f.sensitivity.max);
  s.vx = damp(s.vx, alive ? clamp(input.steer, -1, 1) * f.lateralSpeed * sens : 0, f.steerResponse, dt);
  s.x += s.vx * dt;
  const py = s.y;
  s.y = damp(s.y, f.altitude + 0.6 * Math.sin(s.t * 0.9), 1.5, dt); // concept L1271: ±0.6 m visual bob
  s.vy = (s.y - py) / Math.max(dt, 1e-4);
  s.roll = damp(s.roll, clamp(-s.vx * 0.05, -f.bankMax, f.bankMax), 5, dt);
  s.yaw = Math.atan2(-s.vx, Math.max(s.speed, 1e-3));
  s.pitch = Math.atan2(s.vy, Math.max(s.speed, 1e-3)) * 0.8;
  if (s.z < shiftAt) {
    s.z += PERIOD;
    return PERIOD;
  }
  return 0;
}
